<template>
  <button
    :class="$style.base"
    @mouseleave="resetPreviewRating"
    @keydown.right.prevent="incrementRating"
    @keydown.left.prevent="decrementRating"
  >
    <template v-if="halfValues">
      <svg
        v-for="i in halfValuesList"
        :key="i"
        :class="$style.icon"
        width="56"
        height="53"
        viewbox="0 0 56 53"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          :class="[
            $style['icon-half-values-hover'],
            i + 1 < previewRating || i + 1 <= value ? $style['icon-active'] : {}
          ]"
          d="M27.8 0L35.4515 18.3954L55.1904 20.0385L40.1805 33.0506L44.7282 52.4615L27.8 42.108L10.8718 52.4615L15.4195 33.0506L0.409546 20.0385L20.1484 18.3954L27.8 0Z"
          fill="#D0DAE3"
          @mouseover="changePreviewRating(i + 1)"
          @click="changeRating(i + 1)"
        />
        <path
          :class="[
            $style['icon-half-values-hover'],
            i < previewRating || i <= value ? $style['icon-active'] : {}
          ]"
          d="M27.8 0V42.108L10.8718 52.4615L15.4195 33.0506L0.409546 20.0385L20.1484 18.3954L27.8 0Z"
          fill="#D0DAE3"
          @mouseover="changePreviewRating(i)"
          @click="changeRating(i)"
        />
      </svg>
    </template>
    <template v-else>
      <svg
        v-for="i in wholeValuesList"
        :key="i"
        :class="[$style['icon-whole-hover']]"
        width="56"
        height="53"
        viewbox="0 0 56 53"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        :data-pw="`${name}-${i}`"
        @mouseover="changePreviewRating(i)"
        @click="changeRating(i)"
      >
        <path
          :class="[isActive(i) ? $style['icon-active'] : {}]"
          :data-pw="isActive(i) ? 'active' : ''"
          d="M28.2 0L35.8516 18.3954L55.5905 20.0385L40.5805 33.0506L45.1282 52.4615L28.2 42.108L11.2718 52.4615L15.8196 33.0506L0.809597 20.0385L20.5485 18.3954L28.2 0Z"
          fill="#D0DAE3"
        />
      </svg>
    </template>
  </button>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useField } from 'vee-validate'
import { number } from 'yup'

const props = defineProps({
  modelValue: { type: Number, required: true },
  count: { type: Number, default: 5 },
  halfValues: { type: Boolean, default: false },
  name: { type: String, required: true },
  required: { type: Boolean, default: false }
})

const rules = computed(() => {
  const baseValidation = number()
    .min(0)
    .max(props.halfValues ? props.count * 2 : props.count)
  return props.required ? baseValidation.required() : baseValidation
})

const { value, handleChange, setTouched } = useField(props.name, rules, {
  initialValue: props.modelValue
})
const emits = defineEmits(['update:modelValue'])
const previewRating = ref(0)

const wholeValuesList = computed((): number[] => {
  return [...Array(props.count).keys()].map(i => i + 1)
})

const halfValuesList = computed((): number[] => {
  return [...Array(props.count * 2).keys()]
    .map(i => i + 1)
    .filter(i => i % 2)
})

const isActive = (index: number): boolean => {
  return index < previewRating.value || index <= value.value
}

const changeRating = (rating: number): void => {
  if (value.value === rating) {
    rating--
  }
  setTouched(true)
  handleChange(rating, true)
  emits('update:modelValue', rating)
}

const incrementRating = () => {
  const val = value.value
  const count = props.halfValues ? props.count * 2 : props.count
  if (value.value < count) {
    changeRating(val + 1)
  }
}

const decrementRating = () => {
  const val = value.value
  if (value.value > 0) {
    changeRating(val - 1)
  }
}

const changePreviewRating = (rating: number): void => {
  previewRating.value = rating
}

const resetPreviewRating = (): void => {
  previewRating.value = 0
}
</script>

<style module>
.base {
  box-sizing: border-box;
  padding: 0;
  margin: 0;
  width: 100%;
  margin-top: var(--space-xs);
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  border: none;

  &:focus {
    outline-color: var(--color-pacificblue);
    outline-width: var(--focus-outline-width);
    outline-offset: var(--focus-outline-offset);
  }
}

.icon {
  &:not(:last-of-type) {
    margin-right: var(--space-xxs);
  }
}

.icon-active {
  transition: var(--transition-default);
  fill: var(--color-pacificblue);
}

.icon-half-values-hover {
  @media (hover: hover) {
    & hover {
      transition: var(--transition-default);
      fill: var(--color-pacificblue);
    }
    &:hover path {
      transition: var(--transition-default);
      fill: var(--color-pacificblue);
    }
  }
}

.icon-whole-hover {
  @media (hover: hover) {
    &:hover {
      transition: var(--transition-default);
      transform: scale(1.2);
    }
    &:hover path {
      transition: var(--transition-default);
      fill: var(--color-pacificblue);
    }
  }
}
</style>
